import { axialHexDistance } from '../../../hexmap/coords';
import { getHexTileMapKey } from '../../../hexmap/model/keys';
import type { HexTileMapKey, HexTileType } from '../../../hexmap/model/types';
import { pathTo } from '../../../hexmap/pathfinding';
import { stopKind } from '../../occupancy';
import { redirect } from '../../redirect';
import { stockCities } from '../../stock';
import type { Cell, PlayerId, SimContext, SimState, Unit } from '../../types';
import {
  isMoving,
  isPos,
  type OwnerCache,
  ownerCache,
  ownTerritory,
  reachFor,
  type TickCache,
} from './cache';

/**
 * Retreat: auto workers outside their sheltering territory run into a city
 * from enemy fighters their owner can see (see index.ts).
 */

/** An auto worker outside its territory heads in when a visible enemy fighter is this close… */
const THREAT_RADIUS = 2;
/** …and works only inside it until none is within this distance. */
const SAFE_RADIUS = 3;

// ── Retreat ─────────────────────────────────────────────────────────────────

/** Enemy units that can attack units and that `owner` can see right now (no fog leak). */
function threatsOf(state: SimState, ctx: SimContext, owner: PlayerId, oc: OwnerCache): Cell[] {
  if (!oc.threats) {
    const enemies = Object.values(state.units).filter((u) => {
      // Units stored in a barracks are hidden.
      if (u.owner === owner || u.inside) return false;
      const def = ctx.rules.units[u.type];
      // Rams only hit buildings: nothing to run from.
      return (
        !!def && def.actions.includes('attack') && def.strength > 0 && def.targets !== 'buildings'
      );
    });
    // Skip the vision pass when there's nothing to see.
    const vision = enemies.length > 0 ? oc.derived.vision(owner) : null;
    oc.threats = enemies.filter((u) => vision?.has(getHexTileMapKey(u.pos))).map((u) => u.pos);
  }
  return oc.threats;
}

/**
 * Hex distance from `unit` to the nearest enemy fighter its owner can see;
 * infinite when there's none, or no shelter to run to.
 */
function threatDistance(state: SimState, ctx: SimContext, cache: TickCache, unit: Unit): number {
  const oc = ownerCache(state, ctx, cache, unit.owner);
  const threats = threatsOf(state, ctx, unit.owner, oc);
  if (threats.length === 0) return Number.POSITIVE_INFINITY;
  if (ownTerritory(state, ctx, cache, unit.owner, oc).shelters.size === 0) {
    return Number.POSITIVE_INFINITY;
  }
  let nearest = Number.POSITIVE_INFINITY;
  for (const t of threats) nearest = Math.min(nearest, axialHexDistance(t, unit.pos));
  return nearest;
}

/**
 * Sends `unit` into its nearest reachable own city that takes it (inside:
 * hidden and safe), else to its nearest reachable shelter tile. False if
 * neither is reachable.
 */
function goToShelter(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  unit: Unit,
  terrain: readonly HexTileType[],
  shelters: ReadonlySet<HexTileMapKey>
): boolean {
  const auto = unit.auto;
  if (!auto) return false;
  const reach = reachFor(state, ctx, cache, unit, terrain);
  const occ = cache.derived.occupancy();
  const cities = new Set(
    stockCities(state, ctx.rules, unit.owner).map((c) => getHexTileMapKey(c.pos))
  );
  // Breadth-first order: the first one found is the nearest.
  const best =
    reach.order.find(
      (cell) =>
        cities.has(getHexTileMapKey(cell)) &&
        stopKind(ctx.rules, occ, unit, cell, true) === 'inside'
    ) ?? reach.order.find((cell) => shelters.has(getHexTileMapKey(cell)));
  if (!best) return false;
  // Drop the job (a build order is handed back on return by the construction system).
  delete unit.order;
  redirect(state, unit, pathTo(reach, best));
  auto.target = { q: best.q, r: best.r };
  delete auto.retryTick;
  return true;
}

/**
 * Sends an auto worker outside its territory into its nearest own city (see
 * `goToShelter`) while a visible enemy fighter is within THREAT_RADIUS, and
 * keeps it heading in while one is within SAFE_RADIUS. A worker already in
 * the territory is safe and isn't moved. No extra state: heading for a city
 * (or a territory tile from outside) while threatened is retreating. Returns
 * whether the worker is retreating this tick.
 */
function retreat(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  unit: Unit,
  danger: number
): boolean {
  const auto = unit.auto;
  const def = ctx.rules.units[unit.type];
  if (!auto || !def || danger > SAFE_RADIUS) return false;
  const oc = ownerCache(state, ctx, cache, unit.owner);
  const { shelters } = ownTerritory(state, ctx, cache, unit.owner, oc);
  const heading = isMoving(unit) && auto.target ? auto.target : undefined;
  // On its way into a city: keep going, in the territory too.
  if (heading && stockCities(state, ctx.rules, unit.owner).some((c) => isPos(c, heading))) {
    return true;
  }
  if (shelters.has(getHexTileMapKey(unit.pos))) return false;
  if (heading && shelters.has(getHexTileMapKey(heading))) return true;
  if (danger > THREAT_RADIUS) return false;
  return goToShelter(state, ctx, cache, unit, def.terrain, shelters);
}

export { goToShelter, retreat, SAFE_RADIUS, THREAT_RADIUS, threatDistance, threatsOf };
